import { Checkbox } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Collapse, Row } from 'reactstrap';
// FIXME: use golang/python/node tesseract later on
import Tesseract from 'tesseract.js';
import { ScreenshotItem, useExecutionScreenshots } from '../hooks/useExecutionScreenshots';
import { useStoreActions } from '../stores/store';
import { exportScreenshotsUrl } from '../utils/exportScreenshotsUrl';
import { secondsToTimeString } from '../utils/secondsToTimeString';
import ImageModal from './ImageModal';
import TextModal from './TextModal';

type ExtendedScreenshotItem = ScreenshotItem & {
  ordinal: number;
  timeOnScreenInSeconds: number;
}

function getOrdinal(name: string, prefix: string) {
  const ordinal = +name.toLowerCase().replace(prefix, '').split('.')[0]
  if (isNaN(ordinal)) {
    throw new Error('invalid ordinal for: ' + name)
  }
  return ordinal
}

function getTimeOnScreen(snapshots: ScreenshotItem[], prefix: string, intervalSeconds: number, snapshotIdx: number, startTimeMs = 0) {
  if (snapshotIdx === 0) {
    return 0
  }
  const currOrd = getOrdinal(snapshots[snapshotIdx].screenshotFileName, prefix)
  const prevOrd = getOrdinal(snapshots[snapshotIdx - 1].screenshotFileName, prefix)
  return ((currOrd - prevOrd - 1) * intervalSeconds) + (startTimeMs * 1000)
}

const ExecutionInfoScreenshots: React.FC<{ id: string }> = ({ id }) => {
  const { data } = useExecutionScreenshots(id)
  const screenshots = useMemo<ExtendedScreenshotItem[]>(() => {
    if (!data) {
      return []
    }

    return data.screenshots.map((ss, i) => {
      return {
        ...ss,
        screenshotFileUrl: ss.screenshotFileUrl + `?token=${localStorage.getItem('token')}`,
        ordinal: getOrdinal(ss.screenshotFileName, data.filePrefix),
        timeOnScreenInSeconds: getTimeOnScreen(data.screenshots, data.filePrefix, data.screenshotIntervalSecs, i, data.screenshotStartTimeMs)
      }
    })
  }, [data])

  const priorityScreenshots = useMemo(() => screenshots.slice().sort((a, b) => b.timeOnScreenInSeconds - a.timeOnScreenInSeconds), [screenshots])

  return (
    <>
      <ScreenshotRow
        id={id}
        type="chronological"
        screenshots={screenshots}
        header="Screenshots"
        intervalSeconds={data?.screenshotIntervalSecs || 0}
      />
      <br />
      <ScreenshotRow
        id={id}
        type="priority"
        screenshots={priorityScreenshots}
        intervalSeconds={data?.screenshotIntervalSecs || 0}
        header="Potential Conversations"
      />
      {/* <br /> */}
      {/* <CarouselRow id={id} /> */}
    </>
  );
}

const ScreenshotRow: React.FC<{
  header: string;
  screenshots: ExtendedScreenshotItem[];
  type: "chronological" | "priority";
  id: string;
  intervalSeconds: number
}> = ({ screenshots, header, intervalSeconds, type, id }) => {
  const [selectedFrames, setSelectedFrames] = useState<ExtendedScreenshotItem[]>([])
  const offset = useMemo(() => id === '1' ? 57 : 0, [id])
  const [collapsed, setCollapsed] = useState(true)
  const [txtModalOpen, setTxtModalOpen] = useState(false)
  const [txt, setTxt] = useState('')

  const [imgModalOpen, setImgModalOpen] = useState(false)
  const [img, setImg] = useState('')

  const [imgRecIndex, setImgRecIndex] = useState(-1)
  const [recProgress, setRecProgress] = useState(0)
  const [recognizing, setRecognizing] = useState(false)

  // const [selectedFrames, setSelectedFrames] = useState<ExtendedScreenshotItem[]>([])
  const seekTo = useStoreActions(actions => actions.seekTo)

  const recognizeText = useCallback(async (idx: number) => {
    if (recognizing) {
      window.alert('Please wait!')
      return
    }
    try {
      setRecognizing(true)
      setImgRecIndex(idx)
      const data = await Tesseract.recognize(
        screenshots[idx].screenshotFileUrl,
        'eng',
        {
          logger: (arg: any) => {
            if (arg.status === "recognizing text") {
              setRecProgress(Math.round(arg.progress * 100))
            }
          }
        }
      )
      setTxt(data.data.text)
      setTxtModalOpen(true)
    } catch (error) {
      console.error(error)
      window.alert('something went wrong!')
    } finally {
      setRecognizing(false)
      setRecProgress(0)
      setImgRecIndex(-1)
    }
  }, [recognizing, screenshots])

  const goToTopAndSeek = (ms: number) => {
    window.scrollTo(0, 0)
    seekTo(ms)
  }

  const handleExportScreenshots = () => {
    window.open(exportScreenshotsUrl(selectedFrames.map(s => s.id)))
  }

  return (
    <Row>
      <Col className="order-xl-1">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0">
                  {type === 'chronological' ? `(${screenshots.length}) ` : ''}
                  {header}
                </h3>
              </Col>
              <Col className="text-right" xs="4">
                {
                  !collapsed && selectedFrames.length > 0 &&
                  <Button className="mx-2" color="primary" size="sm" onClick={handleExportScreenshots}>
                    Export Frames
                  </Button>
                }
                {
                  !collapsed &&
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => {
                      if (selectedFrames.length > 0) {
                        setSelectedFrames([])
                      } else {
                        setSelectedFrames(screenshots)
                      }
                    }}
                  >
                    {(selectedFrames.length > 0 ? 'Deselect' : 'Select')} All
                  </Button>
                }
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => {
                    setCollapsed(!collapsed)
                  }}
                >
                  {collapsed ? 'Expand' : 'Collapse'}
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={!collapsed}>
            <CardBody>
              <Row>
                {
                  screenshots.map((s, i) => {
                    const frameTimeMs = (offset * 1000) + (s.ordinal - 1) * 1000 * intervalSeconds

                    return (
                      <Col md="6" lg="4" key={s.ordinal} className="mb-2">
                        <Card className="p-2">
                          <div>
                            <span>
                              At <b>{secondsToTimeString(frameTimeMs / 1000)}</b>
                            </span>
                            <Checkbox
                              style={{
                                margin: 0,
                                padding: 0
                              }}
                              checked={!!selectedFrames.find(f => f.screenshotFileName === s.screenshotFileName)}
                              className="inline float-right form-check-input"
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedFrames([
                                    ...selectedFrames, s
                                  ])
                                } else {
                                  setSelectedFrames(selectedFrames.filter(f => f.ordinal !== s.ordinal))
                                }
                              }}
                            />
                            <br />
                            <div className="my-2">
                              <Button
                                size="sm"
                                color="primary"
                                onClick={() => {
                                  if (type === "chronological") {
                                    goToTopAndSeek(frameTimeMs)
                                  } else {
                                    goToTopAndSeek(frameTimeMs - (s.timeOnScreenInSeconds * 1000))
                                  }
                                }}
                              >
                                {type === "chronological" ? 'Seek Here' : 'Go To Speech'}
                              </Button>
                              <Button
                                size="sm"
                                color="primary"
                                onClick={() => {
                                  recognizeText(i)
                                }}
                              >
                                {
                                  (imgRecIndex === i && recProgress >= 0) ?
                                    `${recProgress}% done`
                                    :
                                    'Get Text'
                                }
                              </Button>
                              <br />
                              {type === 'priority' && <small><b>Screen Time: {secondsToTimeString(s.timeOnScreenInSeconds)}</b></small>}
                            </div>
                          </div>
                          <img
                            src={s.screenshotFileUrl}
                            alt="..."
                            style={{
                              maxWidth: '100%',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setImg(s.screenshotFileUrl)
                              setImgModalOpen(true)
                            }}
                          />
                        </Card>
                      </Col>
                    )
                  })
                }
              </Row>
            </CardBody>
          </Collapse>
        </Card>
        <ImageModal src={img} open={imgModalOpen} toggle={() => setImgModalOpen(!imgModalOpen)} />
        <TextModal text={txt} open={txtModalOpen} toggle={() => setTxtModalOpen(!txtModalOpen)} />
      </Col>
    </Row>
  )
}

// const CarouselRow: React.FC<{ id: string }> = ({ id }) => {
//   const offset = useMemo(() => id === '1' ? 57 : 0, [id])
//   const [activeIndex, setActiveIndex] = useState(0)
//   const [animating, setAnimating] = useState(false)
//   const [collapsed, setCollapsed] = useState(true)
//   const [txtModalOpen, setTxtModalOpen] = useState(false)
//   const [txt, setTxt] = useState('')

//   const [imgRecIndex, setImgRecIndex] = useState(-1)
//   const [recProgress, setRecProgress] = useState(0)
//   const [recognizing, setRecognizing] = useState(false)

//   const seekTo = useStoreActions(actions => actions.seekTo)

//   const snapshots = useMemo(() => id === "1" ? snapshots1 : snapshots2, [id])

//   const recognizeText = useCallback(async (idx: number) => {
//     if (recognizing) {
//       window.alert('Please wait!')
//       return
//     }
//     try {
//       setRecognizing(true)
//       setImgRecIndex(idx)
//       const imgUrl = `/images/${snapshots[idx].name}`
//       const data = await Tesseract.recognize(
//         imgUrl,
//         'eng',
//         {
//           logger: (arg) => {
//             console.log(arg)
//             if (arg.status === "recognizing text") {
//               setRecProgress(Math.round(arg.progress * 100))
//             }
//           }
//         }
//       )
//       setTxt(data.data.text)
//       setTxtModalOpen(true)
//     } catch (error) {
//       console.error(error)
//       window.alert('something went wrong!')
//     } finally {
//       setRecognizing(false)
//       setRecProgress(0)
//       setImgRecIndex(-1)
//     }
//   }, [recognizing, snapshots])

//   const goToTopAndSeek = (ms: number) => {
//     window.scrollTo(0, 0)
//     seekTo(ms)
//   }

//   const next = () => {
//     if (animating) return;
//     const nextIndex = (activeIndex + 1) % snapshots.length
//     setActiveIndex(nextIndex)
//   }

//   const previous = () => {
//     if (animating) return;
//     const prevIndex = activeIndex === 0 ? snapshots.length - 1 : (activeIndex - 1)
//     setActiveIndex(prevIndex)
//   }

//   return (
//     <Row>
//       <Col className="order-xl-1">
//         <Card className="bg-secondary shadow">
//           <CardHeader className="bg-white border-0">
//             <Row className="align-items-center">
//               <Col xs="8">
//                 <h3 className="mb-0">Carousel</h3>
//               </Col>
//               <Col className="text-right" xs="4">
//                 <Button
//                   size="sm"
//                   color="primary"
//                   onClick={() => {
//                     setCollapsed(!collapsed)
//                   }}
//                 >
//                   {collapsed ? 'Expand' : 'Collapse'}
//                 </Button>
//               </Col>
//             </Row>
//           </CardHeader>
//           <Collapse isOpen={!collapsed}>
//             <CardBody>
//               <Row style={{ justifyContent: 'center' }}>
//                 <Carousel
//                   activeIndex={activeIndex}
//                   next={next}
//                   previous={previous}
//                   pause={"hover"}
//                 >
//                   {
//                     snapshots.map((s, i) => (
//                       <CarouselItem
//                         onExiting={() => setAnimating(true)}
//                         onExited={() => setAnimating(false)}
//                         key={s.name}
//                       >
//                         <img style={{ maxWidth: '100%' }} src={`/images/${s.name}`} alt='...' />
//                         <div className="mt-2 text-center">
//                           <Row>
//                             <Col>
//                               <Button size="sm" color="primary" onClick={() => recognizeText(i)}>
//                                 {
//                                   (imgRecIndex === i && recProgress >= 0) ?
//                                     `${recProgress}% done`
//                                     :
//                                     'Get Text'
//                                 }
//                               </Button>
//                               <Button size="sm" color="primary" onClick={() => {
//                                 goToTopAndSeek(offset + (s.ordinal - 1) * 1000 * intervalSeconds)
//                               }}>
//                                 Seek here
//                               </Button>
//                             </Col>
//                           </Row>
//                         </div>
//                       </CarouselItem>
//                     ))
//                   }
//                   <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />
//                   <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
//                 </Carousel>
//               </Row>
//             </CardBody>
//           </Collapse>
//         </Card>
//         <TextModal text={txt} open={txtModalOpen} toggle={() => setTxtModalOpen(!txtModalOpen)} />
//       </Col>
//     </Row>
//   );
// }

export default ExecutionInfoScreenshots
